import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Form,
  Alert,
  FormGroup,
  Badge,
} from "reactstrap";
import { get, put } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Trans } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { 
  SHOW_PARTNER_BRANCH_TIMESLOT,
  UPDATE_PARTNER_BRANCH_TIMESLOT 
} from "helpers/api_endpoints";
import Select from "react-select";

document.title = "Edit Partner Branch Time Slot";

const EditPartnerBranchTimeSlot = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [partnerBranchName, setPartnerBranchName] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    startTime: "",
    endTime: "",
    cutOffTime: "",
    orderStartTime: "",
    expectedOrders: 0,
    type: "regular",
    status: 1,
    recurringDays: [],
    dateRanges: []
  });

  // Day options for recurring days
  const dayOptions = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  // Fetch time slot data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`${SHOW_PARTNER_BRANCH_TIMESLOT}/${id}`);
        const timeSlot = response.data;
        
        // Format time strings to remove seconds for input fields
        const formatTimeForInput = (timeStr) => {
          if (!timeStr) return "";
          return timeStr.slice(0, 5); // Keep only HH:MM
        };

        setFormData({
          partnerBranchId: timeSlot.partnerBranchId,
          startTime: formatTimeForInput(timeSlot.startTime),
          endTime: formatTimeForInput(timeSlot.endTime),
          cutOffTime: formatTimeForInput(timeSlot.cutOffTime),
          orderStartTime: formatTimeForInput(timeSlot.orderStartTime),
          expectedOrders: timeSlot.expectedOrders,
          type: timeSlot.type,
          status: timeSlot.status,
          recurringDays: timeSlot.recurringDays || [],
          dateRanges: timeSlot.dateRanges || (timeSlot.type === "exceptional" ? [{ startDate: "", endDate: "" }] : [])
        });
        setLoading(false);
      } catch (error) {
        setErrorMsg(error?.response?.data?.message || "Failed to load time slot data");
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Helper function to convert time string to minutes since midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Handle adding new date range
  const addDateRange = () => {
    setFormData(prev => ({
      ...prev,
      dateRanges: [...prev.dateRanges, { startDate: "", endDate: "" }]
    }));
  };

  // Handle removing date range
  const removeDateRange = (index) => {
    if (formData.dateRanges.length <= 1) return;
    const newRanges = [...formData.dateRanges];
    newRanges.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      dateRanges: newRanges
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (index, field, value) => {
    const newRanges = [...formData.dateRanges];
    newRanges[index][field] = value;
    setFormData(prev => ({
      ...prev,
      dateRanges: newRanges
    }));
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle select changes
  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    const { startTime, endTime, cutOffTime, orderStartTime, expectedOrders, type } = formData;

    if (!startTime || !endTime || !cutOffTime) {
      setErrorMsg("Start time, end time and cut off time are required");
      return false;
    }

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const cutOffMinutes = timeToMinutes(cutOffTime);
    const orderStartMinutes = orderStartTime ? timeToMinutes(orderStartTime) : null;

    if (startMinutes >= endMinutes) {
      setErrorMsg("Start time must be before end time");
      return false;
    }

    if (cutOffMinutes > endMinutes) {
      setErrorMsg("Cut off time must not be later than end time");
      return false;
    }

    if (orderStartTime && orderStartMinutes >= cutOffMinutes) {
      setErrorMsg("Order start time must be before cut off time");
      return false;
    }

    if (isNaN(expectedOrders) || expectedOrders < 0) {
      setErrorMsg("Expected orders must be a positive number");
      return false;
    }

    if (type === "regular" && formData.recurringDays.length === 0) {
      setErrorMsg("Regular time slots must have at least one recurring day");
      return false;
    }

    if (type === "exceptional") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (const range of formData.dateRanges) {
        if (!range.startDate || !range.endDate) {
          setErrorMsg("All date ranges must have both start and end dates");
          return false;
        }

        const startDate = new Date(range.startDate);
        const endDate = new Date(range.endDate);

        if (startDate > endDate) {
          setErrorMsg("Start date must be before or equal to end date in date ranges");
          return false;
        }

        if (endDate < today) {
          setErrorMsg("Cannot create time slots for past dates");
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const timeSlotData = {
        id: Number(id),
        partnerBranchId: Number(formData.partnerBranchId),
        startTime: formData.startTime + ":00", // Add seconds
        endTime: formData.endTime + ":00",
        cutOffTime: formData.cutOffTime + ":00",
        orderStartTime: formData.orderStartTime ? formData.orderStartTime + ":00" : null,
        expectedOrders: Number(formData.expectedOrders),
        type: formData.type,
        status: Number(formData.status),
        recurringDays: formData.type === "regular" ? formData.recurringDays : null,
        dateRanges: formData.type === "exceptional" ? formData.dateRanges : null
      };

      const response = await put(UPDATE_PARTNER_BRANCH_TIMESLOT, timeSlotData);

      if (response.code === 200) {
        setSuccessMsg("Time slot updated successfully");
          navigate(`/partnersbranch/timeslot`, {
            state: { successMsg: response.message || "Time slot updated successfully" }
          });
      } else {
        setErrorMsg(response.message || "Failed to update time slot");
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message || "Failed to update time slot"
      );
      console.error("Error updating time slot:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.startTime) {
    return <div className="text-center py-5">Loading...</div>;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Partner Branches"
            breadcrumbItem="Edit Partner Branch Time Slot"
          />

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    {successMsg && <Alert color="success">{successMsg}</Alert>}
                    {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                    <Row>
                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Type</Trans>
                        </Label>
                        <Input
                          type="select"
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          required
                        >
                          <option value="regular">Regular</option>
                          <option value="exceptional">Exceptional</option>
                        </Input>
                      </FormGroup>

                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Status</Trans>
                        </Label>
                        <Input
                          type="select"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          required
                        >
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                        </Input>
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Start Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>End Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          name="endTime"
                          value={formData.endTime}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Cut Off Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          name="cutOffTime"
                          value={formData.cutOffTime}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Order Start Time (Optional)</Trans>
                        </Label>
                        <Input
                          type="time"
                          name="orderStartTime"
                          value={formData.orderStartTime}
                          onChange={handleChange}
                        />
                      </FormGroup>

                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Expected Orders</Trans>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          name="expectedOrders"
                          value={formData.expectedOrders}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>

                      {formData.type === "regular" && (
                        <FormGroup className="col-md-6">
                          <Label>
                            <Trans>Recurring Days</Trans>
                          </Label>
                          <Select
                            isMulti
                            options={dayOptions}
                            value={dayOptions.filter(option =>
                              formData.recurringDays.includes(option.value)
                            )}
                            onChange={(selected) =>
                              handleSelectChange(
                                "recurringDays", 
                                selected.map(item => item.value)
                              )
                            }
                            className="basic-multi-select"
                            classNamePrefix="select"
                            required
                          />
                        </FormGroup>
                      )}

                      {formData.type === "exceptional" && (
                        <FormGroup className="col-12">
                          <Label>
                            <Trans>Date Ranges</Trans>
                            <Button
                              color="link"
                              size="sm"
                              onClick={addDateRange}
                              className="float-end"
                            >
                              <i className="mdi mdi-plus"></i> Add Range
                            </Button>
                          </Label>
                          {formData.dateRanges.map((range, index) => (
                            <div key={index} className="mb-3">
                              <Row>
                                <Col md={5}>
                                  <Input
                                    type="date"
                                    value={range.startDate}
                                    onChange={(e) =>
                                      handleDateRangeChange(
                                        index,
                                        "startDate",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </Col>
                                <Col md={5}>
                                  <Input
                                    type="date"
                                    value={range.endDate}
                                    onChange={(e) =>
                                      handleDateRangeChange(
                                        index,
                                        "endDate",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </Col>
                                <Col md={2}>
                                  {formData.dateRanges.length > 1 && (
                                    <Button
                                      color="danger"
                                      outline
                                      onClick={() => removeDateRange(index)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </Button>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          ))}
                        </FormGroup>
                      )}
                    </Row>

                    <div className="mt-4">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? "Updating..." : "Update Time Slot"}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EditPartnerBranchTimeSlot;